// Mock Compliance Data for Demo Mode
import type { ComplianceSummary, AssetComplianceListItem } from '@/types/compliance';

export const mockComplianceSummary: ComplianceSummary = {
  totalAssets: 1250,
  compliantAssets: 980,
  nonCompliantAssets: 145,
  dueSoonAssets: 125,
  unknownStatusAssets: 0,
  compliancePercentage: 78.4,
  trend90Days: 2.3,
  kpis: [
    {
      regimeCode: 'PUWER',
      regimeName: 'Work Equipment Safety (PUWER)',
      totalAssets: 450,
      compliant: 380,
      dueSoon: 45,
      overdue: 25,
      complianceRate: 84.4,
      target: 95.0,
      breachCount: 25
    },
    {
      regimeCode: 'LOLER',
      regimeName: 'Lifting Operations (LOLER)',
      totalAssets: 85,
      compliant: 78,
      dueSoon: 5,
      overdue: 2,
      complianceRate: 91.8,
      target: 100.0,
      breachCount: 2
    },
    {
      regimeCode: 'PAT',
      regimeName: 'Portable Appliance Testing',
      totalAssets: 320,
      compliant: 285,
      dueSoon: 20,
      overdue: 15,
      complianceRate: 89.1,
      target: 95.0,
      breachCount: 15
    },
    {
      regimeCode: 'EICR',
      regimeName: 'Fixed Wiring Inspection',
      totalAssets: 12,
      compliant: 10,
      dueSoon: 1,
      overdue: 1,
      complianceRate: 83.3,
      target: 100.0,
      breachCount: 1
    },
    {
      regimeCode: 'MOT',
      regimeName: 'Vehicle MOT',
      totalAssets: 45,
      compliant: 40,
      dueSoon: 3,
      overdue: 2,
      complianceRate: 88.9,
      target: 100.0,
      breachCount: 2
    },
    {
      regimeCode: 'FIRE_EXTINGUISHER',
      regimeName: 'Fire Extinguisher Service',
      totalAssets: 180,
      compliant: 165,
      dueSoon: 10,
      overdue: 5,
      complianceRate: 91.7,
      target: 100.0,
      breachCount: 5
    },
    {
      regimeCode: 'CALIBRATION',
      regimeName: 'Instrument Calibration',
      totalAssets: 95,
      compliant: 82,
      dueSoon: 8,
      overdue: 5,
      complianceRate: 86.3,
      target: 95.0,
      breachCount: 5
    }
  ],
  byLocation: [
    {
      locationId: 'loc-001',
      locationName: 'London HQ',
      totalAssets: 450,
      compliant: 380,
      complianceScore: 84.4
    },
    {
      locationId: 'loc-002',
      locationName: 'Manchester Office',
      totalAssets: 280,
      compliant: 245,
      complianceScore: 87.5
    },
    {
      locationId: 'loc-003',
      locationName: 'Birmingham Warehouse',
      totalAssets: 320,
      compliant: 250,
      complianceScore: 78.1
    },
    {
      locationId: 'loc-004',
      locationName: 'Edinburgh Branch',
      totalAssets: 120,
      compliant: 95,
      complianceScore: 79.2
    },
    {
      locationId: 'loc-005',
      locationName: 'Leeds Distribution Center',
      totalAssets: 80,
      compliant: 10,
      complianceScore: 12.5
    }
  ],
  byCategory: [
    {
      category: 'Vehicles',
      totalAssets: 45,
      compliant: 40,
      complianceScore: 88.9
    },
    {
      category: 'Lifting Equipment',
      totalAssets: 85,
      compliant: 78,
      complianceScore: 91.8
    },
    {
      category: 'Electrical (Portable)',
      totalAssets: 320,
      compliant: 285,
      complianceScore: 89.1
    },
    {
      category: 'Electrical (Fixed)',
      totalAssets: 12,
      compliant: 10,
      complianceScore: 83.3
    },
    {
      category: 'Fire Safety Equipment',
      totalAssets: 180,
      compliant: 165,
      complianceScore: 91.7
    },
    {
      category: 'Measuring Instruments',
      totalAssets: 95,
      compliant: 82,
      complianceScore: 86.3
    },
    {
      category: 'Machinery',
      totalAssets: 450,
      compliant: 380,
      complianceScore: 84.4
    }
  ]
};

export const mockAssetComplianceList: AssetComplianceListItem[] = [
  {
    assetId: 'asset-001',
    assetName: 'Forklift',
    assetCode: 'FLT-001',
    complianceCheckId: 1,
    category: 'Lifting Equipment',
    location: 'Birmingham Warehouse',
    regimeCode: 'LOLER',
    regimeName: 'Lifting Operations (LOLER)',
    requirementName: '6-month Thorough Examination',
    lastEventDate: '2024-06-15T00:00:00Z',
    nextDueDate: '2024-12-15T00:00:00Z',
    daysUntilDue: 45,
    status: 'due-soon',
    assignedTo: 'John Smith',
    riskBand: 'HIGH',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'SafeLift Inspections Ltd'
  },
  {
    assetId: 'asset-002',
    assetName: 'Overhead Crane',
    assetCode: 'OHC-12',
    complianceCheckId: 1,
    category: 'Lifting Equipment',
    location: 'Birmingham Warehouse',
    regimeCode: 'LOLER',
    regimeName: 'Lifting Operations (LOLER)',
    requirementName: '6-month Thorough Examination',
    lastEventDate: '2024-05-10T00:00:00Z',
    nextDueDate: '2024-11-10T00:00:00Z',
    daysUntilDue: 10,
    status: 'due-soon',
    assignedTo: 'John Smith',
    riskBand: 'CRITICAL',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'SafeLift Inspections Ltd'
  },
  {
    assetId: 'asset-003',
    assetName: 'Pallet Truck',
    assetCode: 'PT-045',
    complianceCheckId: 1,
    category: 'Lifting Equipment',
    location: 'London HQ',
    regimeCode: 'LOLER',
    regimeName: 'Lifting Operations (LOLER)',
    requirementName: '12-month Thorough Examination',
    lastEventDate: '2023-09-20T00:00:00Z',
    nextDueDate: '2024-09-20T00:00:00Z',
    daysUntilDue: -42,
    status: 'overdue',
    assignedTo: 'Sarah Johnson',
    riskBand: 'MEDIUM',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'SafeLift Inspections Ltd'
  },
  {
    assetId: 'asset-004',
    assetName: 'Laptop',
    assetCode: 'DELL-1234',
    complianceCheckId: 2,
    category: 'Computer Equipment',
    location: 'London HQ',
    regimeCode: 'PAT',
    regimeName: 'Portable Appliance Testing',
    requirementName: 'Office IT Equipment Test',
    lastEventDate: '2023-01-15T00:00:00Z',
    nextDueDate: '2025-01-15T00:00:00Z',
    daysUntilDue: 380,
    status: 'compliant',
    assignedTo: 'PAT Testing Team',
    riskBand: 'LOW',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'ElectroSafe PAT Ltd'
  },
  {
    assetId: 'asset-005',
    assetName: 'Extension Lead',
    assetCode: 'EXT-089',
    complianceCheckId: 2,
    category: 'Electrical Equipment',
    location: 'Manchester Office',
    regimeCode: 'PAT',
    regimeName: 'Portable Appliance Testing',
    requirementName: 'Extension Leads Test',
    lastEventDate: '2024-03-10T00:00:00Z',
    nextDueDate: '2025-03-10T00:00:00Z',
    daysUntilDue: 130,
    status: 'compliant',
    assignedTo: 'PAT Testing Team',
    riskBand: 'MEDIUM',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'ElectroSafe PAT Ltd'
  },
  {
    assetId: 'asset-006',
    assetName: 'Power Drill',
    assetCode: 'DRL-234',
    complianceCheckId: 2,
    category: 'Power Tools',
    location: 'Birmingham Warehouse',
    regimeCode: 'PAT',
    regimeName: 'Portable Appliance Testing',
    requirementName: 'Site Power Tools Test',
    lastEventDate: '2024-05-01T00:00:00Z',
    nextDueDate: '2024-11-01T00:00:00Z',
    daysUntilDue: 1,
    status: 'due-soon',
    assignedTo: 'Maintenance Team',
    riskBand: 'HIGH',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'ElectroSafe PAT Ltd'
  },
  {
    assetId: 'asset-007',
    assetName: 'Company Van',
    assetCode: 'VAN-001',
    complianceCheckId: 3,
    category: 'Vehicles',
    location: 'London HQ',
    regimeCode: 'MOT',
    regimeName: 'Vehicle MOT',
    requirementName: 'Annual MOT',
    lastEventDate: '2024-03-15T00:00:00Z',
    nextDueDate: '2025-03-15T00:00:00Z',
    daysUntilDue: 135,
    status: 'compliant',
    assignedTo: 'Fleet Manager',
    riskBand: 'HIGH',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'Halfords Autocentre'
  },
  {
    assetId: 'asset-008',
    assetName: 'Delivery Truck TRK-012',
    category: 'Vehicles',
    location: 'Birmingham Warehouse',
    regimeCode: 'MOT',
    regimeName: 'Vehicle MOT',
    requirementName: 'Annual MOT',
    lastEventDate: '2023-11-20T00:00:00Z',
    nextDueDate: '2024-11-20T00:00:00Z',
    daysUntilDue: 20,
    status: 'due-soon',
    assignedTo: 'Fleet Manager',
    riskBand: 'HIGH',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'Kwik Fit'
  },
  {
    assetId: 'asset-009',
    assetName: 'Fire Extinguisher FE-101',
    category: 'Fire Safety Equipment',
    location: 'London HQ',
    regimeCode: 'FIRE_EXTINGUISHER',
    regimeName: 'Fire Extinguisher Service',
    requirementName: 'Annual Service',
    lastEventDate: '2024-01-10T00:00:00Z',
    nextDueDate: '2025-01-10T00:00:00Z',
    daysUntilDue: 70,
    status: 'compliant',
    assignedTo: 'Facilities Team',
    riskBand: 'HIGH',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'FireSafe Services Ltd'
  },
  {
    assetId: 'asset-010',
    assetName: 'Fire Extinguisher FE-205',
    category: 'Fire Safety Equipment',
    location: 'Birmingham Warehouse',
    regimeCode: 'FIRE_EXTINGUISHER',
    regimeName: 'Fire Extinguisher Service',
    requirementName: 'Annual Service',
    lastEventDate: '2023-10-05T00:00:00Z',
    nextDueDate: '2024-10-05T00:00:00Z',
    daysUntilDue: -26,
    status: 'overdue',
    assignedTo: 'Facilities Team',
    riskBand: 'HIGH',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'FireSafe Services Ltd'
  },
  {
    assetId: 'asset-011',
    assetName: 'Torque Wrench TW-089',
    category: 'Measuring Instruments',
    location: 'Manchester Office',
    regimeCode: 'CALIBRATION',
    regimeName: 'Instrument Calibration',
    requirementName: 'Annual Calibration',
    lastEventDate: '2024-02-20T00:00:00Z',
    nextDueDate: '2025-02-20T00:00:00Z',
    daysUntilDue: 112,
    status: 'compliant',
    assignedTo: 'Quality Team',
    riskBand: 'MEDIUM',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'Precision Calibration Ltd'
  },
  {
    assetId: 'asset-012',
    assetName: 'Gas Detector GD-045',
    category: 'Safety Equipment',
    location: 'Birmingham Warehouse',
    regimeCode: 'CALIBRATION',
    regimeName: 'Instrument Calibration',
    requirementName: 'Annual Calibration',
    lastEventDate: '2023-08-15T00:00:00Z',
    nextDueDate: '2024-08-15T00:00:00Z',
    daysUntilDue: -77,
    status: 'overdue',
    assignedTo: 'Safety Officer',
    riskBand: 'CRITICAL',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'SafetyFirst Calibration'
  },
  {
    assetId: 'asset-013',
    assetName: 'CNC Machine CNC-001',
    category: 'Machinery',
    location: 'Birmingham Warehouse',
    regimeCode: 'PUWER',
    regimeName: 'Work Equipment Safety (PUWER)',
    requirementName: 'Periodic Risk-Based Inspection',
    lastEventDate: '2024-04-10T00:00:00Z',
    nextDueDate: '2024-10-10T00:00:00Z',
    daysUntilDue: -21,
    status: 'overdue',
    assignedTo: 'Maintenance Team',
    riskBand: 'HIGH',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'Industrial Safety Inspections'
  },
  {
    assetId: 'asset-014',
    assetName: 'Lathe Machine LAT-012',
    category: 'Machinery',
    location: 'Manchester Office',
    regimeCode: 'PUWER',
    regimeName: 'Work Equipment Safety (PUWER)',
    requirementName: 'Periodic Risk-Based Inspection',
    lastEventDate: '2024-06-01T00:00:00Z',
    nextDueDate: '2024-12-01T00:00:00Z',
    daysUntilDue: 31,
    status: 'compliant',
    assignedTo: 'Maintenance Team',
    riskBand: 'MEDIUM',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'Industrial Safety Inspections'
  },
  {
    assetId: 'asset-015',
    assetName: 'Electrical Installation - Main Building',
    category: 'Fixed Electrical',
    location: 'London HQ',
    regimeCode: 'EICR',
    regimeName: 'Fixed Wiring Inspection (EICR)',
    requirementName: '5-Year Standard Commercial',
    lastEventDate: '2020-03-15T00:00:00Z',
    nextDueDate: '2025-03-15T00:00:00Z',
    daysUntilDue: 135,
    status: 'compliant',
    assignedTo: 'Facilities Manager',
    riskBand: 'HIGH',
    hasEvidence: true,
    lastResult: 'PASS',
    contractor: 'NICEIC Approved Contractor'
  }
];
